import React, { useEffect, useRef, useState } from 'react';
import { useJazyk } from '../../context/LanguageContext';
import { Lock, ImageOff } from 'lucide-react';
import { mediaUrl } from '../../lib/api';
import { desifrujSoubor } from '../../lib/crypto';

/**
 * Příloha zprávy – obrázek, video nebo hlasovka.
 *
 * U koncově šifrované přílohy stáhne zašifrované bajty, otevře je tady
 * v prohlížeči a udělá z nich dočasnou adresu (blob URL). Server posílá jen
 * změť bajtů a netuší, co v nich je.
 *
 * Dočasná adresa se po odpojení komponenty ruší – jinak by se v paměti
 * hromadily rozšifrované přílohy, což je u fotek zbytečné i nebezpečné.
 */
export default function SecureMedia({ zprava, klic, className = '' }) {
  const { t } = useJazyk();
  const [adresa, setAdresa] = useState(null);
  const [stav, setStav] = useState('nacita');
  const blobRef = useRef(null);

  const jeSifrovana = !!zprava.media_iv;

  useEffect(() => {
    // Nešifrovanou přílohu servíruje server rovnou, stačí adresa s tokenem.
    if (!jeSifrovana) {
      setAdresa(mediaUrl(zprava.media_url));
      setStav('hotovo');
      return undefined;
    }

    if (!klic) { setStav('zamceno'); return undefined; }

    let zivy = true;
    setStav('nacita');
    (async () => {
      try {
        const odpoved = await fetch(mediaUrl(zprava.media_url));
        if (!odpoved.ok) throw new Error('nelze stáhnout');
        const sifra = await odpoved.arrayBuffer();
        const typ = zprava.media_type === 'image' ? 'image/*'
          : zprava.media_type === 'audio' ? 'audio/webm' : 'video/webm';
        const blob = await desifrujSoubor(klic, sifra, zprava.media_iv, typ);
        if (!zivy) return;
        if (!blob) { setStav('chyba'); return; }
        const url = URL.createObjectURL(blob);
        blobRef.current = url;
        setAdresa(url);
        setStav('hotovo');
      } catch {
        if (zivy) setStav('chyba');
      }
    })();

    return () => {
      zivy = false;
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, [zprava.media_url, zprava.media_iv, zprava.media_type, klic, jeSifrovana]);

  if (stav === 'zamceno') {
    return (
      <p className="flex items-center gap-1.5 text-xs italic opacity-70" data-testid="priloha-zamcena">
        <Lock className="w-3.5 h-3.5" /> {t('sif.prilohaZamcena')}
      </p>
    );
  }

  if (stav === 'chyba') {
    return (
      <p className="flex items-center gap-1.5 text-xs italic opacity-70">
        <ImageOff className="w-3.5 h-3.5" /> {t('sif.prilohaChyba')}
      </p>
    );
  }

  if (stav === 'nacita' || !adresa) {
    return <div className="w-40 h-24 rounded-lg bg-black/5 animate-pulse" aria-hidden="true" />;
  }

  if (zprava.media_type === 'image') {
    return <img src={adresa} alt={t('sif.priloha')} className={`rounded-lg max-w-full ${className}`} />;
  }
  if (zprava.media_type === 'audio') {
    return <audio src={adresa} controls className={`max-w-full ${className}`} />;
  }
  return <video src={adresa} controls className={`rounded-lg max-w-full ${className}`} />;
}
